import { memo } from "react";
import { Box, Container, Typography } from "@mui/material";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import GavelIcon from "@mui/icons-material/Gavel";

const Legal = memo(() => {
  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #004B4C 0%, #006668 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            p: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 4, md: 6 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <GavelIcon
              sx={{ fontSize: { xs: 32, md: 48 }, color: "#ffffff", mr: 2 }}
            />
            <Typography
              variant="h2"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                lineHeight: 1.2,
              }}
            >
              Legal Information
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "#ffffff",
              fontSize: { xs: "1rem", md: "1.1rem" },
              opacity: 0.9,
            }}
          >
            Important legal notices and information about SimplerHome
          </Typography>
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            px: { xs: 2, md: 6, lg: 10 },
            py: { xs: 4, md: 6 },
            maxWidth: 1000,
            mx: "auto",
          }}
        >
          {/* Copyright Notice */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 3,
              }}
            >
              Copyright Notice
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
            >
              © {new Date().getFullYear()} SimplerHome. All rights reserved.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8 }}
            >
              All content on this website, including text, graphics, logos,
              images, and software, is the property of SimplerHome or its
              content suppliers and is protected by United States and
              international copyright laws. Unauthorized reproduction,
              distribution, or modification of any content is strictly
              prohibited.
            </Typography>
          </Box>

          {/* Trademarks */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 3,
              }}
            >
              Trademarks
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8 }}
            >
              SimplerHome, the SimplerHome logo, and other SimplerHome marks are
              trademarks or registered trademarks of SimplerHome, Inc. Other
              product and company names mentioned on this site may be trademarks
              of their respective owners. Use of these trademarks without
              express permission is prohibited.
            </Typography>
          </Box>

          {/* Disclaimer */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 3,
              }}
            >
              Disclaimer
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
            >
              The information provided on SimplerHome is for general
              informational purposes only. While we strive to keep the
              information up to date and accurate, we make no representations or
              warranties of any kind, express or implied, about the
              completeness, accuracy, reliability, or suitability of the
              information.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8 }}
            >
              Any reliance you place on such information is strictly at your own
              risk. SimplerHome is not a substitute for professional advice from
              licensed contractors, home inspectors, real estate professionals,
              or legal counsel.
            </Typography>
          </Box>

          {/* Service Provider Disclaimer */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 3,
              }}
            >
              Service Provider Disclaimer
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8 }}
            >
              SimplerHome may provide referrals or connections to third-party
              service providers. We do not endorse, guarantee, or assume
              responsibility for any services provided by third parties. Users
              should independently verify credentials, licensing, and insurance
              of any service provider before engaging their services.
            </Typography>
          </Box>

          {/* Limitation of Liability */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 3,
              }}
            >
              Limitation of Liability
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8 }}
            >
              In no event shall SimplerHome, its directors, employees, partners,
              agents, suppliers, or affiliates be liable for any indirect,
              incidental, special, consequential, or punitive damages, including
              without limitation, loss of profits, data, use, goodwill, or other
              intangible losses, resulting from your access to or use of or
              inability to access or use the service.
            </Typography>
          </Box>

          {/* Governing Law */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 3,
              }}
            >
              Governing Law
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8 }}
            >
              These terms shall be governed and construed in accordance with the
              laws of the United States, without regard to its conflict of law
              provisions. Our failure to enforce any right or provision of these
              terms will not be considered a waiver of those rights.
            </Typography>
          </Box>

          {/* Dispute Resolution */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 3,
              }}
            >
              Dispute Resolution
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8 }}
            >
              Any disputes arising out of or relating to these terms or the use
              of SimplerHome services shall first be attempted to be resolved
              through good-faith negotiation. If negotiation fails, disputes
              shall be resolved through binding arbitration in accordance with
              the rules of the American Arbitration Association.
            </Typography>
          </Box>

          {/* DMCA Notice */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 3,
              }}
            >
              DMCA Notice
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8 }}
            >
              If you believe that content available on SimplerHome infringes
              your copyright, please send a notice to our designated copyright
              agent with: (1) identification of the copyrighted work; (2)
              identification of the infringing material; (3) your contact
              information; (4) a statement of good faith belief; and (5) a
              statement under penalty of perjury that the information is
              accurate.
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box
            sx={{
              p: 4,
              borderRadius: "16px",
              backgroundColor: "#f8f8f8",
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: "primary.main", fontWeight: 600, mb: 3 }}
            >
              Contact Information
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
            >
              For legal inquiries or to report concerns, please contact us:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body1" sx={{ color: "#555", mb: 1 }}>
                <strong>Email:</strong> legal@simplerhome.com
              </Typography>
              <Typography variant="body1" sx={{ color: "#555", mb: 1 }}>
                <strong>Mail:</strong> SimplerHome Legal Department
              </Typography>
              <Typography variant="body1" sx={{ color: "#555" }}>
                Please allow 5-7 business days for a response to written
                inquiries.
              </Typography>
            </Box>
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default Legal;
